<template>
  <div class="page-admin-sync-profiles">
    <div class="page-admin-sync-profiles__content">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-sync-profiles.add')" @click="onAddClick" />
      <div v-if="!isPhone" class="page-admin__table-header">
        <div class="page-admin__count">{{ profiles.length }} {{ $t('menu.admin-sync-profiles') }}</div>
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="refresh" />
          <BaseButton has-icon-left @click.native="onAddClick">
            <BaseIcon name="plus" />
            {{ $t('page.admin-sync-profiles.add') }}
          </BaseButton>
        </div>
      </div>

      <div class="page-admin-sync-profiles__table-wrapper">
        <TableNormal
          :headers="headers"
          :items="profiles"
          :items-per-page="-1"
          :loading="pending"
          :loading-text="$t('page.admin-sync-profiles.loading')"
          :no-data-text="$t('page.admin-sync-profiles.no-data')"
          hide-default-footer
          class="page-admin-sync-profiles__table"
          @click:row="onEditClick"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count> {{ profiles.length }} {{ $t('menu.admin-sync-profiles') }} </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="refresh" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>

          <template v-slot:item.type="{ item }">
            <div class="page-admin-sync-profiles__type-cell">
              <BaseIcon :name="getEntitySyncIcon(item, null)" class="page-admin-sync-profiles__crm-icon" />
              <span>{{ getProfileTypeLabel(item.type) }}</span>
            </div>
          </template>

          <template v-slot:item.syncUrl="{ item }">
            <span class="page-admin-sync-profiles__url" :title="item.syncUrl">{{ item.syncUrl }}</span>
          </template>

          <template v-slot:item.active="{ item }">
            <BaseTag preset="soft" :tone="getSyncProfileActiveTone(item.active)">
              {{ item.active ? $t('basic-words.active') : $t('basic-words.inactive') }}
            </BaseTag>
          </template>

          <template v-slot:item.quoteSyncEnabled="{ item }">
            <span>{{ item.quoteSyncEnabled ? $t('basic-words.yes') : $t('basic-words.no') }}</span>
          </template>

          <template v-slot:item.autoSync="{ item }">
            <span>{{ item.autoSync ? $t('basic-words.yes') : $t('basic-words.no') }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.name"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <div v-else class="page-admin-sync-profiles__actions-cell" @click.stop>
              <BaseRoundButton
                v-if="supportsFieldMappings(item.type)"
                :size="36"
                icon="list"
                is-white
                :tooltip-text="$t('page.admin-sync-profiles.manage-mappings')"
                @click.native="onManageMappingsClick(item)"
              />
              <BaseRoundButton
                :size="36"
                icon="edit-2"
                is-white
                :tooltip-text="$t('actions.edit')"
                @click.native="onEditClick(item)"
              />
              <BaseRoundButton
                :size="36"
                icon="trash-2"
                is-white
                is-red
                :tooltip-text="$t('actions.delete')"
                @click.native="onDeleteClick(item)"
              />
            </div>
          </template>
        </TableNormal>
      </div>
    </div>

    <SyncProfileDialog ref="syncProfileDialog" @action-succeeded="refresh" @delete-requested="onDeleteClick" />
    <DeleteSyncProfileDialog ref="deleteSyncProfileDialog" @action-succeeded="refresh" />
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseTag from '@/components/base/BaseTag.vue';
import DeleteSyncProfileDialog from '@/components/page-admin-sync-profiles/DeleteSyncProfileDialog';
import SyncProfileDialog from '@/components/page-admin-sync-profiles/SyncProfileDialog';
import TableNormal from '@/components/table/TableNormal.vue';
import { paths } from '@/util/paths';
import { getSyncProfileActiveTone } from '@/util/tag-tones/sync-profile-active-tag';
import { supportsFieldMappings, getProfileTypeLabel } from '@/util/sync-profile-util';
import { getEntitySyncIcon } from '@/util/entity-sync-util';
import { mapActions, mapGetters } from 'vuex';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageAdminSyncProfiles',
  components: {
    PageMainAction,
    TableNormal,
    BaseButton,
    BaseIcon,
    BaseLoadingBar,
    BaseMoreActions,
    BaseRefreshButton,
    BaseRoundButton,
    BaseTag,
    DeleteSyncProfileDialog,
    SyncProfileDialog,
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('adminSyncProfiles', ['profiles', 'pending']),
    headers() {
      return [
        { text: this.$t('page.admin-sync-profiles.columns.name'), value: 'name' },
        { text: this.$t('page.admin-sync-profiles.columns.type'), value: 'type', phone: 'meta' },
        {
          text: this.$t('page.admin-sync-profiles.columns.sync-url'),
          value: 'syncUrl',
          phone: 'subtitle',
          width: '220px',
        },
        { text: this.$t('page.admin-sync-profiles.columns.active'), value: 'active', phone: 'meta', width: '100px' },
        { text: this.$t('page.admin-sync-profiles.columns.quote-sync'), value: 'quoteSyncEnabled', width: '120px' },
        { text: this.$t('page.admin-sync-profiles.columns.auto-sync'), value: 'autoSync', width: '110px' },
        { text: this.$t('page.tenders.data-table-titles.actions'), value: 'actions', sortable: false, width: '130px' },
      ];
    },
  },
  methods: {
    getSyncProfileActiveTone,
    supportsFieldMappings,
    getProfileTypeLabel,
    getEntitySyncIcon,
    ...mapActions('adminSyncProfiles', ['getSyncProfiles']),
    refresh() {
      this.getSyncProfiles().catch(() => this.$toast.error(this.$t('page.admin-sync-profiles.load-error')));
    },
    onAddClick() {
      this.$refs.syncProfileDialog.openDialog();
    },
    onEditClick(profile) {
      this.$refs.syncProfileDialog.openDialog(profile);
    },
    onDeleteClick(profile) {
      this.$refs.syncProfileDialog.closeDialog();
      this.$refs.deleteSyncProfileDialog.openDialog(profile);
    },
    onManageMappingsClick(profile) {
      this.$router.push(paths.adminSyncProfileFieldMappings.replace(':profileId', profile.id));
    },
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's buttons do the same.
    rowActions(profile) {
      return [
        ...(supportsFieldMappings(profile.type)
          ? [{ key: 'mappings', label: this.$t('page.admin-sync-profiles.manage-mappings'), icon: 'list' }]
          : []),
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        { key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true },
      ];
    },
    onRowAction(key, profile) {
      if (key === 'mappings') this.onManageMappingsClick(profile);
      else if (key === 'edit') this.onEditClick(profile);
      else this.onDeleteClick(profile);
    },
  },
  created() {
    this.refresh();
  },
};
</script>

<style lang="scss">
.page-admin-sync-profiles {
  &__content {
    padding: 32px;
  }

  &__table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__type-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__crm-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &__url {
    display: block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-family: monospace;
  }

  &__actions-cell {
    display: flex;
    justify-content: flex-end;
    gap: 4px;
  }

  &__table tbody tr {
    cursor: pointer;
  }

  .v-data-table.theme--light tbody td {
    color: $color-normal;
  }

  .v-data-table__progress {
    .base-loading-bar {
      position: absolute;
      top: 48px;
    }
  }
}
</style>
